import React, { useState } from 'react';
import { Wallet, Info, Calculator } from 'lucide-react';
import { Input } from './ui/input';
import { formatCurrency } from '../lib/utils';

const PLATFORM_FEE_PERCENT = 20;
const TRAINER_SHARE_PERCENT = 100 - PLATFORM_FEE_PERCENT;

/**
 * Earnings breakdown + estimator. Renders ONLY for trainers
 * (caller is responsible for gating visibility).
 */
const TrainerEarningsBreakdown = ({ defaultRate = 75, variant = 'card' }) => {
  const [rate, setRate] = useState(String(defaultRate));
  const num = Number(rate);
  const validRate = Number.isFinite(num) && num >= 40;
  const earnings = validRate ? (num * TRAINER_SHARE_PERCENT) / 100 : null;
  const platformCut = validRate ? (num * PLATFORM_FEE_PERCENT) / 100 : null;

  const wrapperBase =
    variant === 'pitch'
      ? 'bg-[#0d0d10] border border-[#C0A062]/30 rounded-lg p-6'
      : 'bg-[#18181B] border border-zinc-800 rounded-lg p-6';

  return (
    <div className={wrapperBase} data-testid="trainer-earnings-breakdown">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-5 h-5 text-[#C0A062]" />
        <h2 className="text-lg font-bold text-white">How you get paid</h2>
      </div>

      <ul className="space-y-2.5 text-sm text-zinc-300 mb-6">
        <li className="flex gap-2">
          <span className="text-[#C0A062]">·</span>
          DojoDash charges a {PLATFORM_FEE_PERCENT}% platform fee on every session.
        </li>
        <li className="flex gap-2">
          <span className="text-[#C0A062]">·</span>
          You keep {TRAINER_SHARE_PERCENT}% of every booking.
        </li>
        <li className="flex gap-2">
          <span className="text-[#C0A062]">·</span>
          Example: a $100 session pays you $80, directly to your bank account via Stripe.
        </li>
        <li className="flex gap-2">
          <span className="text-[#C0A062]">·</span>
          Payouts are processed through Stripe Connect. You must connect your bank account to receive payments.
        </li>
        <li className="flex gap-2">
          <span className="text-[#C0A062]">·</span>
          No monthly fees, no subscription costs, no hidden charges. You only pay the platform fee when a session is booked and completed.
        </li>
        <li className="flex gap-2">
          <span className="text-[#C0A062]">·</span>
          If a client cancels more than 24 hours before the session, you receive nothing (client gets a full refund). If a client cancels within 24 hours or no-shows, you keep the full amount.
        </li>
      </ul>

      <div className="border-t border-zinc-800 pt-5">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-4 h-4 text-[#C0A062]" />
          <h3 className="text-sm font-semibold text-white">Earnings estimator</h3>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs text-zinc-400 block mb-1.5" htmlFor="earnings-rate">
              Your session rate
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <Input
                id="earnings-rate"
                type="number"
                min="40"
                step="1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="75"
                className="bg-[#09090B] border-zinc-800 text-white pl-7"
                data-testid="earnings-rate-input"
              />
            </div>
          </div>
          <div className="text-right sm:text-left sm:pl-2">
            <p className="text-xs text-zinc-500">Your earnings</p>
            <p
              className="text-[#C0A062] text-2xl font-bold leading-tight"
              data-testid="earnings-takehome"
            >
              {earnings !== null ? formatCurrency(earnings) : '—'}
              <span className="text-xs text-zinc-500 font-normal"> /session</span>
            </p>
          </div>
        </div>
        {validRate ? (
          <p className="text-[11px] text-zinc-500 mt-2" data-testid="earnings-detail">
            Platform fee: {formatCurrency(platformCut)} · You take home: {formatCurrency(earnings)} per session.
          </p>
        ) : (
          <p className="text-[11px] text-red-400 mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Minimum session rate is $40.
          </p>
        )}
      </div>
    </div>
  );
};

export default TrainerEarningsBreakdown;
